import React, { Suspense, useState } from 'react'
import clsx from 'clsx'

import { useEmojiContext } from 'stream-chat-react'
import { useMessageContext } from 'stream-chat-react'

import type { NimbleEmojiProps } from 'emoji-mart'
import type { ReactionResponse } from 'stream-chat'

import type { DefaultStreamChatGenerics } from '../../types'
import { useProcessReactions } from './hooks/useProcessReactions'

export type SimpleReactionsListProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  additionalEmojiProps?: Partial<NimbleEmojiProps>
  handleReaction?: (reactionType: string, event: React.BaseSyntheticEvent) => Promise<void>
  own_reactions?: ReactionResponse<StreamChatGenerics>[]
  reaction_counts?: { [key: string]: number }
  reactions?: ReactionResponse<StreamChatGenerics>[]
}

const UnMemoizedSimpleReactionsList = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: SimpleReactionsListProps<StreamChatGenerics>
) => {
  const { handleReaction: propHandleReaction, ...rest } = props

  const { Emoji, emojiConfig } = useEmojiContext('SimpleReactionsList')
  const { handleReaction: contextHandleReaction } =
    useMessageContext<StreamChatGenerics>('SimpleReactionsList')

  const {
    additionalEmojiProps,
    emojiData,
    emojiByType,
    getEmojiByReactionType,
    iHaveReactedWithReaction,
    latestReactions,
    latestReactionTypes,
    reactionCounts,
  } = useProcessReactions({ emojiConfig, ...rest })

  const [tooltipReactionType, setTooltipReactionType] = useState<string | undefined>('')

  const handleReaction = propHandleReaction || contextHandleReaction

  if (!latestReactions.length) return null

  // Don't filter out reactions - show all reactions including native emojis
  // if (!supportedReactionsArePresent) return null

  return (
    <div className="flex items-center gap-1">
      <ul
        className="flex items-center gap-1"
        data-testid="simple-reaction-list"
        onMouseLeave={() => setTooltipReactionType(undefined)}
      >
        {latestReactionTypes.map((reactionType, i) => {
          const emojiObject = getEmojiByReactionType(reactionType)
          const isOwnReaction = iHaveReactedWithReaction(reactionType)

          // Check if this is an encoded emoji reaction (starts with emoji_)
          const isEncodedEmoji = reactionType.startsWith('emoji_')
          const displayEmoji = isEncodedEmoji ? emojiByType[reactionType] : null

          // For native emoji reactions (short strings that aren't predefined)
          const isNativeEmoji = reactionType.length <= 4 && !emojiObject && !isEncodedEmoji
          const count = reactionCounts?.[reactionType] || 0

          // Show if it's a predefined emoji, native emoji, or encoded emoji
          return emojiObject || isNativeEmoji || displayEmoji ? (
            <li
              className={clsx('str-chat__simple-reactions-list-item', {
                'str-chat__message-reaction-own': isOwnReaction,
              })}
              key={`${emojiObject?.id || reactionType}-${i}`}
              onClick={(event) => handleReaction(reactionType, event)}
              onKeyUp={(event) => handleReaction(reactionType, event)}
            >
              <span onMouseEnter={() => setTooltipReactionType(reactionType)}>
                {displayEmoji ? (
                  // Display emoji from encoded reaction
                  <span className="text-base">{displayEmoji}</span>
                ) : isNativeEmoji ? (
                  // Display native emoji directly
                  <span className="text-base">{reactionType}</span>
                ) : (
                  // Display custom emoji using emoji-mart
                  <Suspense fallback={null}>
                    <Emoji
                      data={emojiData}
                      emoji={emojiObject}
                      size={13}
                      {...additionalEmojiProps}
                    />
                  </Suspense>
                )}
              </span>
              {count > 0 && <span className="ml-1 text-xs font-semibold">{count}</span>}
              {tooltipReactionType === (emojiObject?.id || reactionType) && (
                <div className="str-chat__simple-reactions-list-tooltip str-chat__tooltip">
                  <div className="arrow" />
                </div>
              )}
            </li>
          ) : null
        })}
      </ul>
    </div>
  )
}

export const SimpleReactionsList = React.memo(
  UnMemoizedSimpleReactionsList
) as typeof UnMemoizedSimpleReactionsList
